import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  concat,
  concatMap,
  defer,
  EMPTY,
  exhaustMap,
  filter,
  map,
  Observable,
  of,
  OperatorFunction,
  switchMap,
  tap,
} from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { ToastService } from '@ps/core-overlay';
import {
  AdvancedFeasibilityFilters,
  Survey,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';
import {
  StandardSurveyPayloadInput,
  SurveyService,
} from '@ps/survey-data-access';
import { Nullable } from '@ps/utils';

import { SurveyForm } from '../../utils';

import {
  selectFeasibilityCpi,
  selectPricing,
  SurveyCreationInsightsActions,
} from '../insights';

import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from './survey-creation-audience.actions';
import {
  selectAdvancedFeasibilityFilters,
  selectSurveyFormState,
  selectSurveyId,
} from './survey-creation-audience.selectors';

// Form + pricing read off the store before any gate runs — surveyId may be null.
interface SurveyFormContext {
  readonly surveyId: Nullable<number>;
  readonly form: SurveyForm;
  readonly pricing: SurveyPricingSnapshot;
  readonly advancedFeasibilityFilters: AdvancedFeasibilityFilters;
}

// Same shape once the save gate has confirmed the id — and the form's data with it, so no
// stage downstream has to re-assert that a validated form really does carry one.
interface ValidatedSurveyForSave {
  readonly surveyId: number;
  readonly data: NonNullable<SurveyForm['data']>;
  readonly pricing: SurveyPricingSnapshot;
  readonly advancedFeasibilityFilters: AdvancedFeasibilityFilters;
}

// Returns trigger actions only — a follow-up's own HTTP runs in its own effect,
// so its failures map to its own failure action, not a save failure.
type SurveySaveFollowUp = (survey: Survey) => Observable<Action>;

// This *is* StandardSurveyPayloadInput plus the surveyId only the URL path needs.
interface SurveyUpdateRequest extends StandardSurveyPayloadInput {
  readonly surveyId: number;
}

// Owns the create/save/clone lifecycle for a standard survey — split out of
// SurveyCreationAudienceEffects to isolate this concern, same as insights/advanced-filters.
@Injectable()
export class SurveyCreationSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);

  private readonly _surveyService = inject(SurveyService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);

  public readonly createStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.createStandardSurvey),
      this._gatherSurveyFormContext(),
      tap(({ form }) => this._reportFormErrors(form, true)),
      // Creating requires the absence of an id; saving requires one.
      filter(({ surveyId, form }) => !surveyId && this._isSubmittable(form)),
      map(({ form, pricing }) => {
        const user = this._actingUser();
        const { metadata, qualifications, quotas } = Option.from(
          form.data,
        ).unwrap();

        return {
          metadata,
          pricing,
          userId: user.id,
          companyId: user.companyId,
          targeting: { qualifications, quotas },
        } satisfies StandardSurveyPayloadInput;
      }),
      exhaustMap((input) =>
        this._surveyService.createStandardSurvey(input).pipe(
          map((survey) => {
            this._toastService.success('Survey successfully saved', {
              keepOpen: false,
            });
            return SurveyCreationApiActions.createStandardSurveySuccess({
              survey,
            });
          }),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationApiActions.createStandardSurveyFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly saveStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.saveSurvey),
      // Only a plain save stays on the page, so only it refreshes insights.
      this._saveSurveyPipeline(() =>
        of(SurveyCreationInsightsActions.refreshAfterSave()),
      ),
    ),
  );

  public readonly saveAndCreateNewSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.saveAndCreateNew),
      this._saveSurveyPipeline(() =>
        this._navigateTo(APP_ROUTES.CREATE_SURVEY),
      ),
    ),
  );

  public readonly saveAndCloneSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.saveAndCloneSurvey),
      this._saveSurveyPipeline((survey) => this._cloneSurvey(survey.id)),
    ),
  );

  // Reset before navigating so the edit page loads fresh data, not stale
  // state. concat + defer guarantees that order — tap-then-map would only
  // work by accident of Router deferring navigation to a microtask.
  public readonly navigateToEditSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationApiActions.createStandardSurveySuccess,
        SurveyCreationApiActions.cloneSurveySuccess,
        SurveyCreationApiActions.createSurveyFromTemplateSuccess,
      ),
      switchMap(({ survey }) =>
        concat(
          of(SurveyCreationActions.reset()),
          defer(() => this._navigateTo(APP_ROUTES.editSurvey(survey.id))),
        ),
      ),
    ),
  );

  // Stage 0 — reads form + pricing off the store; each caller applies its own surveyId gate.
  private _gatherSurveyFormContext(): OperatorFunction<
    Action,
    SurveyFormContext
  > {
    return (trigger$) =>
      trigger$.pipe(
        concatLatestFrom(() => [
          this._store.select(selectSurveyId),
          this._store.select(selectSurveyFormState),
          this._store.select(selectPricing),
          this._store.select(selectFeasibilityCpi),
          this._store.select(selectAdvancedFeasibilityFilters),
        ]),
        map(
          ([
            ,
            surveyId,
            formState,
            pricingState,
            feasibilityCpi,
            advancedFeasibilityFilters,
          ]) => ({
            surveyId,
            form: SurveyForm.from(formState),
            pricing: {
              ...pricingState,
              feasibilityCpi,
            } satisfies SurveyPricingSnapshot,
            advancedFeasibilityFilters,
          }),
        ),
      );
  }

  // Runs validate → build → persist for every save-* action. followUp must
  // return trigger actions only — its own HTTP stays in its own effect.
  private _saveSurveyPipeline(
    followUp: SurveySaveFollowUp,
  ): OperatorFunction<Action, Action> {
    return (trigger$) =>
      trigger$.pipe(
        this._validateSurveyForSave(),
        this._buildSurveyUpdateRequest(),
        this._persistSurveyUpdate(followUp),
      );
  }

  // Stage 1 — gates on an existing, submittable survey, reporting errors if not.
  private _validateSurveyForSave(): OperatorFunction<
    Action,
    ValidatedSurveyForSave
  > {
    return (trigger$) =>
      trigger$.pipe(
        this._gatherSurveyFormContext(),
        tap(({ form }) => this._reportFormErrors(form)),
        // One pipeline instead of a filter paired with throwing unwraps: the id and the form's
        // data are only produced where both are known to exist, so the gate and the narrowing
        // cannot drift apart.
        concatMap(({ surveyId, form, pricing, advancedFeasibilityFilters }) =>
          Option.from(surveyId)
            .filter(() => this._isSubmittable(form))
            .andThen((validatedSurveyId) =>
              Option.from(form.data).map((data) => ({
                surveyId: validatedSurveyId,
                data,
                pricing,
                advancedFeasibilityFilters,
              })),
            )
            .match({
              some: (validated) => of(validated),
              none: () => EMPTY,
            }),
        ),
      );
  }

  // Stage 2 — builds the exact input SurveyService.updateStandardSurvey expects.
  private _buildSurveyUpdateRequest(): OperatorFunction<
    ValidatedSurveyForSave,
    SurveyUpdateRequest
  > {
    return (context$) =>
      context$.pipe(
        map(({ surveyId, data, pricing, advancedFeasibilityFilters }) => {
          const user = this._actingUser();
          const { metadata, qualifications, quotas } = data;

          return {
            surveyId,
            metadata,
            pricing,
            userId: user.id,
            companyId: user.companyId,
            targeting: { qualifications, quotas },
            advancedFeasibilityFilters,
          };
        }),
      );
  }

  // Stage 3 — persists, reports the outcome, then chains the caller's follow-up.
  private _persistSurveyUpdate(
    followUp: SurveySaveFollowUp,
  ): OperatorFunction<SurveyUpdateRequest, Action> {
    return (request$) =>
      request$.pipe(
        switchMap(({ surveyId, ...input }) =>
          this._surveyService.updateStandardSurvey(surveyId, input).pipe(
            tap(() =>
              this._toastService.success('Survey successfully saved', {
                keepOpen: false,
              }),
            ),
            switchMap((survey) =>
              concat(
                of(SurveyCreationApiActions.saveSurveySuccess({ survey })),
                followUp(survey),
              ),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationApiActions.saveSurveyFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
        ),
      );
  }

  // Emits cloneSurveySuccess/Failure so navigateToEditSurvey$ (and any other listener) reacts
  // the same way regardless of the caller.
  private _cloneSurvey(surveyId: number): Observable<Action> {
    return this._surveyService
      .cloneStandardSurvey(surveyId, this._authSelectors.hasImpersonatedBuyer())
      .pipe(
        map((survey) => {
          this._toastService.success('Survey successfully cloned', {
            keepOpen: false,
          });
          return SurveyCreationApiActions.cloneSurveySuccess({ survey });
        }),
        this.psCatchError(
          (error) =>
            of(
              SurveyCreationApiActions.cloneSurveyFailure({
                error: error.message,
              }),
            ),
          { notify: true },
        ),
      );
  }

  private _isSubmittable(form: SurveyForm): boolean {
    return form.valid && form.isStandardSurvey();
  }

  // Toasts form errors. Creating passes true to stay quiet until the required
  // metadata is filled in; saving reports whatever is already invalid.
  private _reportFormErrors(
    form: SurveyForm,
    onlyWhenMetadataComplete = false,
  ): void {
    if (form.showErrors(onlyWhenMetadataComplete)) {
      Option.from(form.error).inspect((message) =>
        this._toastService.error(message),
      );
    }
  }

  // Navigates as a side effect and contributes no action to the stream.
  private _navigateTo(route: string): Observable<never> {
    this._router.navigate([route]);
    return EMPTY;
  }

  private _actingUser(): { id: number; companyId: number } {
    const user = this._authSelectors.user().unwrap();
    return {
      id: user.id,
      companyId: this._authSelectors.activeCompanyId(),
    };
  }
}
